import { Link } from "wouter";
import { FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link href="/signup">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 -ml-2 mb-4">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Terms &amp; Conditions</h1>
          </div>
          <p className="text-sm text-slate-500">Last updated: January 1, 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">1. Introduction</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Welcome to our platform. These Terms &amp; Conditions govern your use of our AI chatbot platform and
              associated services. By creating an account or using our services, you agree to be bound by these
              terms. Please read them carefully before proceeding.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              These terms apply to all users of the platform, including administrators, members, and guests.
              If you do not agree to any part of these terms, you may not access or use our services.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Account Usage</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials. Each account
              is for a single user or organization and may not be shared with third parties. You agree to notify
              us immediately of any unauthorized use of your account.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>One account per person or organization unless otherwise agreed in writing.</li>
              <li>You must provide accurate and complete registration information.</li>
              <li>Accounts may not be transferred to another party without prior written consent.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">3. User Responsibilities</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              You agree to use the platform only for lawful purposes and in accordance with these Terms. You
              are solely responsible for all content, data, and configurations you submit through the platform.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">You agree not to:</p>
            <ul className="mt-2 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>Use the platform to engage in illegal, harmful, or fraudulent activities.</li>
              <li>Attempt to reverse-engineer, decompile, or disassemble any part of the service.</li>
              <li>Upload malicious code, viruses, or any content that could harm the platform or other users.</li>
              <li>Impersonate any person or entity or misrepresent your affiliation.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Subscription Terms</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Access to certain features requires a paid subscription. Subscription fees are billed in advance
              on a monthly or annual basis, depending on your chosen plan. All fees are non-refundable unless
              explicitly stated in our refund policy.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>Subscriptions renew automatically unless cancelled before the renewal date.</li>
              <li>We reserve the right to change pricing with at least 30 days' notice to active subscribers.</li>
              <li>Downgrading your plan may result in loss of access to certain features.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">5. Service Usage</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Our platform is provided "as is" and we make no warranties, express or implied, regarding its
              availability, reliability, or fitness for a particular purpose. We reserve the right to modify,
              suspend, or discontinue any part of the service at any time.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              We implement reasonable technical measures to maintain service availability but cannot guarantee
              uninterrupted access. Scheduled maintenance windows will be communicated in advance where possible.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Termination</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Either party may terminate the agreement at any time. You may cancel your account at any time
              through your account settings. We reserve the right to suspend or terminate accounts that violate
              these Terms, engage in abusive behavior, or remain inactive for an extended period.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              Upon termination, your right to access the platform ceases immediately. Data retention following
              termination is governed by our Privacy Policy and applicable law.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. Contact Information</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              If you have any questions about these Terms &amp; Conditions, please contact our support team:
            </p>
            <div className="mt-3 p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-1">
              <p><strong>Email:</strong> legal@example.com</p>
              <p><strong>Address:</strong> 123 Platform Street, Suite 100, San Francisco, CA 94103</p>
              <p><strong>Response time:</strong> We aim to respond within 2 business days.</p>
            </div>
          </section>

        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          By using our platform you acknowledge that you have read, understood, and agree to these Terms &amp; Conditions.
        </p>
      </div>
    </div>
  );
}
